export const CommonMistakes = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-red-50 border border-red-200 rounded-full mb-6">
            <span className="text-red-600 text-sm font-semibold">Avoid These Errors</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Common T12 Mistakes That Cost Sellers Money
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Learn from others' mistakes. These common errors can reduce your sale price by tens or hundreds of thousands of dollars.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 font-bold text-xl">1</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Mixing Personal and Property Expenses</h3>
                <p className="text-gray-700 mb-3">
                  Including personal expenses in your T12 inflates operating costs and reduces NOI, directly lowering your property value.
                </p>
                <div className="bg-red-50 rounded-lg p-4 mb-3">
                  <p className="text-sm text-gray-700 mb-2"><strong>Example:</strong></p>
                  <p className="text-sm text-gray-700">Owner includes $15,000 in personal vehicle expenses and home office costs in T12. Buyer removes these, increasing expenses and reducing NOI by $15,000 = $230,769 lower value at 6.5% cap rate.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Solution:</p>
                  <p className="text-sm text-gray-700">Keep separate books for personal and property expenses. Only include costs directly related to property operations.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 font-bold text-xl">2</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Using Scheduled Rent Instead of Collected Rent</h3>
                <p className="text-gray-700 mb-3">
                  Reporting what tenants should pay rather than what they actually paid creates a false picture that buyers will quickly uncover.
                </p>
                <div className="bg-red-50 rounded-lg p-4 mb-3">
                  <p className="text-sm text-gray-700 mb-2"><strong>Example:</strong></p>
                  <p className="text-sm text-gray-700">Scheduled rent: $360,000. Actual collected: $330,000. When buyers verify against bank statements, they discover $30,000 discrepancy = $461,538 overvaluation. Deal falls apart or price drops significantly.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Solution:</p>
                  <p className="text-sm text-gray-700">Always use actual collected rent from bank deposits. Be transparent about collection issues and factor them into vacancy loss.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 font-bold text-xl">3</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Omitting Property Management Fees</h3>
                <p className="text-gray-700 mb-3">
                  Self-managing owners often don't include management fees in T12, thinking it shows better NOI. Buyers always add this expense back.
                </p>
                <div className="bg-red-50 rounded-lg p-4 mb-3">
                  <p className="text-sm text-gray-700 mb-2"><strong>Example:</strong></p>
                  <p className="text-sm text-gray-700">Your T12 NOI: $200,000. Buyer adds 8% management fee ($27,520) = Adjusted NOI of $172,480. Your expected value: $3,076,923. Buyer's offer: $2,653,538 = $423,385 less than expected.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Solution:</p>
                  <p className="text-sm text-gray-700">Include market-rate management fees (8-10%) in your T12 even if you self-manage. This gives buyers accurate NOI and prevents surprises.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 font-bold text-xl">4</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Including Capital Expenditures as Operating Expenses</h3>
                <p className="text-gray-700 mb-3">
                  Major one-time improvements like new roofs or HVAC systems shouldn't be in T12 operating expenses. This artificially inflates expenses and reduces NOI.
                </p>
                <div className="bg-red-50 rounded-lg p-4 mb-3">
                  <p className="text-sm text-gray-700 mb-2"><strong>Example:</strong></p>
                  <p className="text-sm text-gray-700">Including $50,000 roof replacement in T12 expenses reduces NOI by $50,000 = $769,231 lower property value. Buyers will adjust this out, but it creates confusion and distrust.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Solution:</p>
                  <p className="text-sm text-gray-700">Separate capital expenditures from operating expenses. Provide a separate CapEx schedule showing major improvements made during ownership.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 font-bold text-xl">5</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Inconsistent Accounting Methods</h3>
                <p className="text-gray-700 mb-3">
                  Changing how you categorize income or expenses month-to-month makes T12 impossible to analyze and raises red flags about accuracy.
                </p>
                <div className="bg-red-50 rounded-lg p-4 mb-3">
                  <p className="text-sm text-gray-700 mb-2"><strong>Example:</strong></p>
                  <p className="text-sm text-gray-700">Parking income sometimes in "rental income," sometimes in "other income." Maintenance costs split between "repairs" and "general expenses" inconsistently. Buyers can't verify numbers and lose confidence.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Solution:</p>
                  <p className="text-sm text-gray-700">Use consistent categories and accounting methods throughout the 12-month period. Create a chart of accounts and stick to it.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-red-600 font-bold text-xl">6</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Hiding or Minimizing Deferred Maintenance</h3>
                <p className="text-gray-700 mb-3">
                  Showing artificially low maintenance expenses to boost NOI backfires when buyers inspect the property and discover needed repairs.
                </p>
                <div className="bg-red-50 rounded-lg p-4 mb-3">
                  <p className="text-sm text-gray-700 mb-2"><strong>Example:</strong></p>
                  <p className="text-sm text-gray-700">T12 shows $12,000 annual maintenance (3.5% of income). Property inspection reveals $80,000 in deferred maintenance. Buyer reduces offer by $80,000 plus adds $20,000/year to normalized expenses = $387,692 total reduction.</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Solution:</p>
                  <p className="text-sm text-gray-700">Address deferred maintenance before listing or be transparent about needed repairs. Provide realistic maintenance expense history.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">More Common T12 Mistakes</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Not Reconciling to Tax Returns</div>
                  <p className="text-sm text-gray-700">T12 income significantly different from Schedule E raises fraud concerns</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Rounding Numbers Too Much</div>
                  <p className="text-sm text-gray-700">Everything ending in $00 or $000 looks fabricated rather than actual</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Including One-Time Income</div>
                  <p className="text-sm text-gray-700">Insurance claims, legal settlements inflate income unsustainably</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Forgetting Seasonal Expenses</div>
                  <p className="text-sm text-gray-700">Snow removal, seasonal landscaping must be included in annual totals</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Using Pro Forma Instead of Actual</div>
                  <p className="text-sm text-gray-700">Projecting what could happen rather than reporting what did happen</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Not Accounting for Vacancy</div>
                  <p className="text-sm text-gray-700">Showing 100% occupancy when you had vacant units during the year</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Incomplete Utility Expenses</div>
                  <p className="text-sm text-gray-700">Missing water, sewer, or trash bills that are landlord responsibility</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Waiting Until Listing to Compile T12</div>
                  <p className="text-sm text-gray-700">Scrambling to create T12 leads to errors and missing documentation</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Not Having Supporting Documentation</div>
                  <p className="text-sm text-gray-700">Can't provide bank statements, receipts, or invoices to verify T12</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Ignoring Buyer Questions</div>
                  <p className="text-sm text-gray-700">Defensive or evasive responses to T12 questions kill buyer confidence</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-6 text-center">The Cost of T12 Mistakes</h3>
          
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold mb-2">15-30%</div>
              <div className="text-sm opacity-90">Average price reduction when T12 has major errors or red flags</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold mb-2">60%</div>
              <div className="text-sm opacity-90">Of deals that fall apart cite financial documentation issues as primary reason</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold mb-2">$500K+</div>
              <div className="text-sm opacity-90">Potential lost value on a $3M property due to poor T12 presentation</div>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-lg opacity-90">
              The good news? All of these mistakes are completely avoidable with proper planning, accurate record-keeping, and professional guidance.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};